import type { CubeQueryRecord } from '@/dev/types/cubeQueryTypes';
import { loadState, saveState } from '@/dev/services/queryStorage';

export class QueryStore {
  private queries: CubeQueryRecord[] = [];

  private readonly maxQueries = 1000;

  private listeners = new Set<() => void>();

  private isRecording = false;

  private recordingStartedAt: number | null = null;

  private persistTimer: ReturnType<typeof setTimeout> | null = null;

  private handlePageHide = (): void => {
    this.persistState();
  };

  constructor() {
    const state = loadState();
    this.isRecording = state.isRecording;
    this.queries = state.queries;
    if (typeof window !== 'undefined') window.addEventListener('pagehide', this.handlePageHide);
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener();
      } catch {}
    });
  }

  persistState(): void {
    if (this.persistTimer !== null) {
      clearTimeout(this.persistTimer);
      this.persistTimer = null;
    }
    saveState({ isRecording: this.isRecording, queries: this.queries });
  }

  private schedulePersist(): void {
    if (this.persistTimer !== null) return;

    this.persistTimer = setTimeout(() => {
      this.persistTimer = null;
      this.persistState();
    }, 500);
  }

  setRecording(recording: boolean): void {
    if (recording && !this.isRecording) {
      this.recordingStartedAt = Date.now();
    }
    if (!recording) {
      this.recordingStartedAt = null;
    }
    this.isRecording = recording;
  }

  getRecording(): boolean {
    return this.isRecording;
  }

  getRecordingStartedAt(): number | null {
    return this.recordingStartedAt;
  }

  addQuery(record: CubeQueryRecord): void {
    if (this.queries.length < this.maxQueries) {
      this.queries.push(record);
      this.schedulePersist();
      this.notifyListeners();
    }
  }

  getQueries(): CubeQueryRecord[] {
    return [...this.queries];
  }

  clearQueries(): void {
    this.queries = [];
    this.persistState();
    this.notifyListeners();
  }

  deleteQuery(queryId: string): void {
    this.queries = this.queries.filter(q => q.id !== queryId);
    this.persistState();
    this.notifyListeners();
  }

  getMaxQueries(): number {
    return this.maxQueries;
  }

  getCurrentCount(): number {
    return this.queries.length;
  }

  isFull(): boolean {
    return this.queries.length >= this.maxQueries;
  }
}
